<script lang="ts" setup>
import { GpTextArea, GpTextInput } from '@gportal/uikit';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import type { CreateUserSSHKeyResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import type { SSHKey } from 'gpcore-grpc/gpcore/type/v1/ssh_key_pb';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import CommandText from 'gpcore-uikit/components/Component.Command.Text.vue';
import onModalShow from 'gpcore-uikit/composables/onModalShow';
import useFormData from 'gpcore-uikit/composables/useFormData';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    show: boolean;
  }>(),
  {
    show: false,
  },
);
const emit = defineEmits<{
  (e: 'created', sshKey: SSHKey);
  (e: 'close');
}>();

const initialValues = {
  name: '',
  publicKey: '',
};

const { form, resetForm } = useFormData(initialValues);

const handleClose = () => {
  emit('close');
};

const userStore = useUserStore();
const tryAutoFillSSHKeyName = (value: string) => {
  form.publicKey = value;

  if (form.name.length === 0) {
    const keyParts = form.publicKey.split(' ');
    if (keyParts.length > 2) {
      for (let i = 2; i < keyParts.length; i++) {
        form.name += ` ${keyParts[i]}`;
      }
    }
    form.publicKey = form.publicKey.replaceAll(form.name, '');
  }
};

const isValid = computed(() => form.name.length > 0 && form.publicKey.length > 0);
const save = () => {
  if (!isValid.value) {
    return undefined;
  }
  return userStore.createSSHKey(form);
};

const onDone = (response: CreateUserSSHKeyResponse) => {
  form.name = '';
  form.publicKey = '';
  emit('created', response.sshKey);
};

onModalShow(props, () => {
  resetForm();
});
</script>

<template>
  <AsyncModal
    :show="show"
    :header-icon="['fas', 'fa-key']"
    header-text="Add new public SSH key"
    :async-function="save"
    :is-valid="isValid"
    confirm-text="Add"
    @close="handleClose"
    @done="onDone"
  >
    <template #content>
      <div class="tw-mb-4 tw-px-4">
        Generate a
        <ExternalLink url="https://www.man7.org/linux/man-pages/man1/ssh-keygen.1.html">
          new SSH key pair
        </ExternalLink>
        and add the public part of it here. If you already have a private key, you can show the
        public part of the key with <CommandText>ssh-keygen -f private-key -y</CommandText>.
        Copy/Paste the output in the text field below.
      </div>
      <div class="tw-mb-8 tw-px-4">
        <GpTextArea
          :model-value="form.publicKey"
          :required="true"
          class="tw-mb-4"
          label="Public SSH key"
          name="key"
          input-id="add-ssh-key-key"
          @update:modelValue="tryAutoFillSSHKeyName"
        />
        <template v-if="form.publicKey.length > 0">
          <div class="tw-mb-4 tw-mt-8">
            Set a distinctive name to the key to identify it later. The name is only visible to you.
          </div>
          <GpTextInput
            v-if="form.publicKey.length > 0"
            v-model="form.name"
            :required="true"
            label="Name"
            name="name"
            input-id="add-ssh-key-name"
          />
        </template>
      </div>
    </template>
  </AsyncModal>
</template>
